import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Opportunity } from '@vendasta/sales-opportunities';
import { Observable } from 'rxjs';
import { filter, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
  contractDurationKeysToEnum,
  ContractDurationTranslationKey,
  MIN_CONTRACT_DURATION_REGEX,
} from '../../../../../../interface';
import { OpportunityService } from '../../../../../../opportunity.service';

@Component({
  selector: 'sales-ui-contract-duration-selector',
  templateUrl: './contract-duration-selector.component.html',
  standalone: false,
})
export class ContractDurationSelectorComponent implements OnInit {
  protected opportunity$: Observable<Opportunity>;
  protected contractDurationTranslations: ContractDurationTranslationKey[] = contractDurationKeysToEnum;
  protected contractDurationForm: FormGroup;
  private readonly destroyRef = inject(DestroyRef);

  // If provided contract duration embeds in parent form
  protected isEmbeddedForm: boolean;
  private _parentForm?: FormGroup;
  @Input()
  set parentForm(value: FormGroup | undefined) {
    this._parentForm = value;
    this.isEmbeddedForm = !!value;
  }
  get parentForm(): FormGroup | undefined {
    return this._parentForm;
  }

  constructor(
    private readonly updateService: OpportunityService,
    private readonly snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    if (this.isEmbeddedForm) {
      this.setFormGroup(null);
    } else {
      this.opportunity$ = this.updateService.currentOpportunity$;
      this.opportunity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((opportunity) => {
        this.setFormGroup(opportunity);
        this.setValueUpdateSubscription();
      });
    }
  }
  private setFormGroup(opportunity: Opportunity): void {
    const duration = opportunity?.expectedContractDuration?.value || 0;
    const period = opportunity?.expectedContractDuration?.duration || 0;
    this.contractDurationForm = new FormGroup({
      contractDurationValue: new FormControl(duration, [validateContractDurationValue(MIN_CONTRACT_DURATION_REGEX)]),
      contractDurationPeriod: new FormControl(period, []),
    });

    if (this.isEmbeddedForm) {
      this.parentForm.addControl('contractDuration', this.contractDurationForm);
    }
  }

  private setValueUpdateSubscription(): void {
    this.contractDurationForm.valueChanges
      .pipe(
        withLatestFrom(this.opportunity$),
        filter(
          ([formValue, opportunity]) =>
            this.contractDurationForm.dirty &&
            opportunity &&
            formValue.contractDurationValue &&
            formValue.contractDurationPeriod,
        ),
        switchMap(([formValue, opportunity]) => {
          const duration = {
            value: formValue.contractDurationValue,
            duration: formValue.contractDurationPeriod,
          };
          return this.updateService.updateExpectedContractDuration(duration, opportunity);
        }),
        take(1),
      )
      .subscribe((newOpp) => {
        if (newOpp) {
          this.snackbarService.openSuccessSnack('SALES_CONTRACT_DURATION.DURATION_UPDATED');
        } else {
          this.snackbarService.openErrorSnack('SALES_CONTRACT_DURATION.ERRORS.UNABLE_TO_UPDATE_DURATION');
        }
      });
  }
}

function validateContractDurationValue(r: string): ValidatorFn {
  const regexp = new RegExp(r);
  return (c: FormControl): ValidationErrors | null => {
    if (c.value == null || c.value == undefined || c.value == '' || c.value == 0 || c.value == '0') return null;
    return regexp.test(c.value)
      ? null
      : {
          validateContractDurationValue: {
            valid: false,
          },
        };
  };
}
